import { useEffect, useState, useMemo } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle, STATUS_COLOR } from "./_roadsCommon";

/** Ch 21.2 — Vertical Profile Editor. Linked to a horizontal alignment. */
export default function ProfileEditorPanel({ projectId }) {
  const [alignments, setAlignments] = useState([]);
  const [alignmentId, setAlignmentId] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [active, setActive] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { (async () => {
    try {
      const r = await api.get(`/projects/${projectId}/roads/alignments`);
      setAlignments(r.data.alignments || []);
      if (r.data.alignments?.[0]) setAlignmentId(r.data.alignments[0].id);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  })(); /* eslint-disable-next-line */ }, []);

  useEffect(() => { if (alignmentId) loadProfiles(); /* eslint-disable-next-line */ }, [alignmentId]);

  const loadProfiles = async () => {
    try {
      const { data } = await api.get(
        `/projects/${projectId}/roads/profiles?alignment_id=${alignmentId}`);
      setProfiles(data.profiles || []);
      setActive(data.profiles?.[0] || null);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const createProfile = async () => {
    const a = alignments.find((x) => x.id === alignmentId);
    if (!a) return;
    const total = a.geometry?.total_length_m || 1000;
    const def = [
      { chainage_m: 0, elevation_m: 100 },
      { chainage_m: total / 2, elevation_m: 110 },
      { chainage_m: total, elevation_m: 105 },
    ];
    try {
      const { data } = await api.post(`/projects/${projectId}/roads/profiles`,
        { alignment_id: alignmentId, vpis: def });
      setActive(data.profile);
      loadProfiles();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const updateVPIs = async (vpis) => {
    if (!active) return;
    try {
      const { data } = await api.put(
        `/projects/${projectId}/roads/profiles/${active.id}`,
        { alignment_id: alignmentId, vpis });
      setActive({ ...active, vpis, geometry: data.geometry });
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const editVPI = (idx, key, val) => {
    const v = active.vpis.map((p, i) =>
      i === idx ? { ...p, [key]: parseFloat(val) || 0 } : p);
    setActive({ ...active, vpis: v });
  };

  const addVPI = () => {
    if (!active) return;
    const last = active.vpis[active.vpis.length - 1];
    updateVPIs([...active.vpis, { chainage_m: last.chainage_m + 500,
                                     elevation_m: last.elevation_m }]);
  };

  const delVPI = (idx) => {
    if (active.vpis.length <= 2) return;
    updateVPIs(active.vpis.filter((_, i) => i !== idx));
  };

  // ── viewBox for profile (X = chainage, Y = elev exaggerated) ──
  const { viewBox, scaleY, minE, maxE, totalLen } = useMemo(() => {
    const vp = active?.vpis || [];
    if (!vp.length) return { viewBox: "0 0 1000 200", scaleY: 1, minE: 0, maxE: 100, totalLen: 1000 };
    const xs = vp.map((p) => p.chainage_m), ys = vp.map((p) => p.elevation_m);
    const minX = 0, maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const padE = (maxY - minY) * 0.5 + 5;
    const sY = 8;   // 8x vertical exaggeration
    return { viewBox: `${minX - 30} ${-(maxY + padE) * sY} ${maxX + 60} ${(maxY - minY + padE * 2) * sY}`,
              scaleY: sY, minE: minY, maxE: maxY, totalLen: maxX };
  }, [active?.vpis]);

  if (!alignments.length) {
    return <div style={{ padding: 24, color: "var(--pf-muted)", fontSize: 12 }}>
      Create a horizontal alignment first (Alignment Editor).</div>;
  }

  const checks = active?.geometry?.checks || [];
  const segs   = active?.geometry?.segments || [];

  return (
    <div data-testid="profile-editor" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <select data-testid="profile-alignment-select" style={inputStyle}
          value={alignmentId} onChange={(e) => setAlignmentId(e.target.value)}>
          {alignments.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <div style={{ height: 6 }} />
        {!active ? (
          <button data-testid="profile-new" style={{ ...btn, width: "100%" }} onClick={createProfile}>
            + Create profile
          </button>
        ) : (
          <>
            <div style={{ ...label, marginBottom: 4 }}>VPIs</div>
            <table style={{ width: "100%", fontSize: 10, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
                  <th style={{ textAlign: "right", padding: 3 }}>Chain.</th>
                  <th style={{ textAlign: "right", padding: 3 }}>Elev.</th>
                  <th style={{ textAlign: "right", padding: 3 }}>Lvc</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {active.vpis.map((p, i) => (
                  <tr key={i} data-testid={`profile-vpi-${i}`}
                      style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                    <td><input style={{ ...inputStyle, padding: 1, fontSize: 10 }} type="number"
                                  value={p.chainage_m}
                                  onChange={(e) => editVPI(i, "chainage_m", e.target.value)}
                                  onBlur={() => updateVPIs(active.vpis)} /></td>
                    <td><input style={{ ...inputStyle, padding: 1, fontSize: 10 }} type="number" step="0.1"
                                  value={p.elevation_m}
                                  onChange={(e) => editVPI(i, "elevation_m", e.target.value)}
                                  onBlur={() => updateVPIs(active.vpis)} /></td>
                    <td><input style={{ ...inputStyle, padding: 1, fontSize: 10, width: 40 }} type="number"
                                  value={p.vertical_curve_length_m || ""} placeholder="–"
                                  onChange={(e) => editVPI(i, "vertical_curve_length_m", e.target.value)}
                                  onBlur={() => updateVPIs(active.vpis)} /></td>
                    <td><button style={{ ...btnGhost, padding: "1px 5px", fontSize: 10 }}
                                  onClick={() => delVPI(i)}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button data-testid="profile-add-vpi" style={{ ...btnGhost, width: "100%", marginTop: 6 }}
                      onClick={addVPI}>+ VPI</button>
          </>
        )}
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 6, fontSize: 11, color: "var(--pf-muted)", borderBottom: "1px solid var(--pf-border)" }}>
          Profile · {totalLen}m · elev {minE.toFixed(1)}–{maxE.toFixed(1)}m · vertical exaggeration ×{scaleY}
        </div>
        <svg viewBox={viewBox} data-testid="profile-canvas"
              style={{ width: "100%", height: "calc(100% - 32px)", background: "#0a1220" }}>
          {/* Design profile */}
          {(active?.vpis || []).map((p, i) => i === 0 ? null : (
            <line key={i}
                    x1={active.vpis[i-1].chainage_m} y1={-active.vpis[i-1].elevation_m * scaleY}
                    x2={p.chainage_m} y2={-p.elevation_m * scaleY}
                    stroke="#3aa0ff" strokeWidth={2} />
          ))}
          {(active?.vpis || []).map((p, i) => (
            <g key={i} transform={`translate(${p.chainage_m}, ${-p.elevation_m * scaleY})`}
                data-testid={`profile-marker-${i}`}>
              <circle r={6} fill="#F26B1F" stroke="#fff" strokeWidth={1.5} />
              <text y={-12} fontSize={10} textAnchor="middle" fill="#fff" fontFamily="monospace">
                {p.elevation_m.toFixed(1)}
              </text>
            </g>
          ))}
          {/* Gradient labels */}
          {segs.map((g, i) => (
            <text key={i} x={(g.from_chainage_m + g.to_chainage_m) / 2}
                    y={-((g.from_elevation_m + g.to_elevation_m) / 2) * scaleY - 2}
                    fontSize={9} textAnchor="middle" fill="#aaa" fontFamily="monospace">
              {g.gradient_pct > 0 ? "+" : ""}{g.gradient_pct.toFixed(2)}%
            </text>
          ))}
        </svg>
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Vertical curve checks</div>
        <div data-testid="profile-checks" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {checks.length === 0 && (
            <div style={{ fontSize: 10, color: "#22c55e" }}>✓ No interior VPIs to check</div>
          )}
          {checks.map((c, i) => (
            <div key={i} style={{ padding: 6, fontSize: 10, borderRadius: 4,
                background: STATUS_COLOR[c.status] + "22",
                borderLeft: `3px solid ${STATUS_COLOR[c.status]}` }}>
              <div><b>{c.type.toUpperCase()}</b> @ ch {c.vpi_chainage_m}m</div>
              <div>g₁={c.g_in_pct}% → g₂={c.g_out_pct}% · A={c.algebraic_diff_pct}%</div>
              <div>K={c.K_value} · K_min={c.K_min} · L={c.vertical_curve_length_m}m</div>
              <div style={{ color: STATUS_COLOR[c.status], fontWeight: 600 }}>{c.status}</div>
            </div>
          ))}
        </div>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
